import { formatDate } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  PlusCircle,
  Eye,
  Edit,
  Trash2,
  Users as UsersIcon,
  List,
  Search,
} from "lucide-react";
import { DataTable } from "@/components/ui/data-table";
import apiClient from "../../api/apiClient";
import { all_routes } from "../../Router/all_routes";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { API_ENDPOINTS } from "../../api/header";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard, FilterSearch } from "@/components/ui/filter-card";
import { TableCard } from "@/components/ui/table-card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical } from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import RecordNotFound from "@/core/components/RecordNotFound";

const Users = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchText, setSearchText] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedEmployeeName, setSelectedEmployeeName] = useState("");
  const [storeEmployees, setStoreEmployees] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };

  // Queries
  const { data: userData, isLoading: isLoadingUsers } = useQuery({
    queryKey: ["employees", currentPage, pageSize, searchText],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.USERS.EMPLOYEES, {
        params: {
          page: currentPage,
          per_page: pageSize,
          search: searchText,
        },
      });
      return res.data;
    },
    enabled: hasPermission("employee read"),
  });

  const employees = userData?.data || [];
  const paginationMeta = {
    current_page: userData?.meta?.current_page || userData?.current_page || 1,
    last_page: userData?.meta?.last_page || userData?.last_page || 1,
    per_page: userData?.meta?.per_page || userData?.per_page || 10,
    total: userData?.meta?.total || userData?.total || employees.length,
  };

  const { data: designations = [] } = useQuery({
    queryKey: ["designations"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.USERS.DESIGNATIONS);
      return res.data.data || [];
    },
    enabled: hasPermission("employee designations read"),
  });

  const { data: stores = [] } = useQuery({
    queryKey: ["stores"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.STORES.STORES);
      return res.data.data || [];
    },
  });

  const { data: storeAssignments = [] } = useQuery({
    queryKey: ["store_assignments"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.STORES.STORE_EMPLOYEE);
      return res.data.data || [];
    },
  });

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      await apiClient.delete(`${API_ENDPOINTS.USERS.EMPLOYEES}/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employees"] });
      toast.success("Employee deleted successfully.");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to delete employee");
    },
  });

  const displayStoreAssignments = (employeeId, employeeName) => {
    const assignments = storeAssignments.filter(
      (assignment) => assignment.employee_id === employeeId,
    );
    setStoreEmployees(assignments);
    setSelectedEmployeeName(employeeName);
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  const handleEdit = (id) => {
    navigate(all_routes.editEmployee.replace(":id", id));
  };

  useEffect(() => {
    if (!isModalVisible) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setStoreEmployees([]);
    }
  }, [isModalVisible]);

  const columns = [
    {
      accessorKey: "full_name",
      header: "Name",
      sortingFn: "text",
    },

    {
      accessorKey: "phone",
      header: "Phone",
      cell: ({ getValue }) => getValue() || "-",
    },
    {
      accessorKey: "email",
      header: "Email",
      sortingFn: "text",
    },
    {
      accessorKey: "address",
      header: "Address",
      cell: ({ getValue }) => getValue() || "-",
    },
    {
      accessorKey: "created_at",
      header: "Created At",
      cell: ({ getValue }) => formatDate(getValue()),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ getValue }) => (
        <Badge variant={getValue() === "active" ? "success" : "destructive"}>
          {getValue() === "active" ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {hasPermission("employee read") && (
              <DropdownMenuItem
                onClick={() =>
                  displayStoreAssignments(
                    row.original.id,
                    row.original.full_name,
                  )
                }
              >
                <Eye className="mr-2 h-4 w-4" /> Assignments
              </DropdownMenuItem>
            )}
            {hasPermission("employee update") && (
              <DropdownMenuItem onClick={() => handleEdit(row.original.id)}>
                <Edit className="mr-2 h-4 w-4" /> Edit
              </DropdownMenuItem>
            )}
            {hasPermission("employee delete") && (
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => handleDelete(row.original.id)}
              >
                <Trash2 className="mr-2 h-4 w-4" /> Delete
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  if (!hasPermission("employee read")) {
    return <RecordNotFound />;
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title="Employees List"
          subtitle="Manage Your Employees"
        />
        <PageHeaderActions>
          {hasPermission("employee create") && (
            <Button onClick={() => navigate(all_routes.addEmployee)}>
              <PlusCircle className="mr-2 h-4 w-4" />
              Add New Employee
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <FilterCard>
        <FilterSearch
          placeholder="Search employees..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </FilterCard>

      <TableCard>
        <DataTable
          columns={columns}
          data={employees}
          pagination={{
            pageIndex: currentPage - 1,
            pageSize: pageSize,
          }}
          onPaginationChange={(updater) => {
            if (typeof updater === "function") {
              const newState = updater({
                pageIndex: currentPage - 1,
                pageSize,
              });
              setCurrentPage(newState.pageIndex + 1);
              setPageSize(newState.pageSize);
            }
          }}
          pageCount={paginationMeta.last_page}
          isLoading={isLoadingUsers}
          enableRowSelection={true}
          footerInfo={
            <span className="font-medium text-foreground">
              Total Employees: {paginationMeta.total || 0}
            </span>
          }
        />
      </TableCard>

      <Dialog open={isModalVisible} onOpenChange={setIsModalVisible}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Store Assignments - {selectedEmployeeName}
            </DialogTitle>
          </DialogHeader>

          <div className="px-1 py-4">
            {storeEmployees.length > 0 ? (
              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Store Name</TableHead>
                      <TableHead>Designation</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {storeEmployees.map((assignment) => {
                      const store = stores.find(
                        (s) => s.id === assignment.store_id,
                      );
                      const designation = designations.find(
                        (d) => d.id === assignment.employee_designation_id,
                      );
                      return (
                        <TableRow key={assignment.id}>
                          <TableCell className="font-medium">
                            {store
                              ? store.name
                              : `Store ID: ${assignment.store_id}`}
                          </TableCell>
                          <TableCell>{designation?.name || "-"}</TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                assignment.status === 1
                                  ? "success"
                                  : "destructive"
                              }
                            >
                              {assignment.status === 1 ? "Active" : "Inactive"}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  No store assignments found for this employee.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setIsModalVisible(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};

export default Users;
